import { PublicContentShareStatusResponse, ShareStatus, SmartShareLinkResponse, SmartShareType } from "@authory-packages/core";
import { useRouter } from "next/router";
import { usePublicContentItemAccessKeyContext } from "../../context/publicContentItemAccessKeyContext";
import { getSharedContentItemQuerie, getSmartSharesLinkQuerie } from "../../types/queries";
import { getPublicArticle, getPublicSmartShareArticle } from "../../api/articles";
import { useQuery, UseQueryResult } from "@tanstack/react-query";
import { getSmartShareLink } from "../../api/smart-share";

export const getPublicContentItemClient = (
    isStandaloneContentItemView: boolean,
    useSmartShareEndpoint: boolean,
    contentItemShareStatus?: PublicContentShareStatusResponse
) => {
    const router = useRouter();
    const authorSlug = router.query.author as string;
    const smartShareUrl = router.query.smartShareURL as string;
    const routeContentItemAccessKey = router.query.contentItemAccessKey as string;
    const token = router.query.c ? (router.query.c as string) : null;

    const { contentItemResolveKey, contentItemAccessKey } = usePublicContentItemAccessKeyContext();

    let isCollectionSharedAndNotPassword = false;
    let isCollectionSharedAndHasPasswordAndVisitorSetPassword = false;

    if (isStandaloneContentItemView && contentItemShareStatus) {
        const isCollectionShared = contentItemShareStatus.shareStatus !== ShareStatus.Unpublished;
        isCollectionSharedAndNotPassword = isCollectionShared && !contentItemShareStatus.isPasswordProtected;
        const isCollectionSharedAndHasPassword = isCollectionShared && contentItemShareStatus.isPasswordProtected;
        isCollectionSharedAndHasPasswordAndVisitorSetPassword = isCollectionSharedAndHasPassword && !!contentItemAccessKey?.length;
    }

    // profile collection page fetches straight away, however standalone collection pages depends on publishing status
    const isContentItemReadyToFetch =
        !isStandaloneContentItemView || isCollectionSharedAndNotPassword || isCollectionSharedAndHasPasswordAndVisitorSetPassword;
    const contentItemResolveKeyToUse = isStandaloneContentItemView ? (contentItemResolveKey as string) : routeContentItemAccessKey;

    const itemQueryQuery = useSmartShareEndpoint
        ? getSharedContentItemQuerie(authorSlug, smartShareUrl, "")
        : getSharedContentItemQuerie(authorSlug, contentItemResolveKeyToUse, contentItemAccessKey);

    // Get the item
    const item = useQuery({
        queryKey: itemQueryQuery,
        queryFn: () =>
            useSmartShareEndpoint
                ? getPublicSmartShareArticle(itemQueryQuery[1]!, itemQueryQuery[2]!)
                : getPublicArticle(itemQueryQuery[1]!, itemQueryQuery[2]!, itemQueryQuery[3]!, token),
        enabled: isContentItemReadyToFetch,
        throwOnError: false
    });

    let contentSmartShareLink: UseQueryResult<SmartShareLinkResponse, Error> | null = null;

    if (useSmartShareEndpoint) {
        const smartShareLinkQuerie = getSmartSharesLinkQuerie(authorSlug, smartShareUrl, SmartShareType.ContentItem);

        contentSmartShareLink = useQuery({
            queryKey: smartShareLinkQuerie,
            queryFn: () => getSmartShareLink(smartShareLinkQuerie[1], smartShareLinkQuerie[2], smartShareLinkQuerie[3]),
            throwOnError: false,
            enabled: isContentItemReadyToFetch
        });
    }

    return { item, contentSmartShareLink };
};
